import {DateTime} from 'luxon';
import {DateFilter, RelativeDateRule} from "../dates";
import {
    FilterDefinition,
    FilterType,
    FilterValueByType,
    NonEmptyArray,
    NumberFilter,
    NumberOperator
} from "./types";

type JsonRelativeDateAnchor = { type: 'now' } | { type: 'date'; value: string };

type JsonRelativeDateRule =
    | {
    kind: 'bounded';
    direction: 'last' | 'next' | 'current';
    amount?: number;
    unit: 'day' | 'week' | 'month';
    anchor?: JsonRelativeDateAnchor;
}
    | {
    kind: 'open';
    direction: 'before' | 'after';
    amount: number;
    unit: 'day' | 'week' | 'month';
    anchor?: JsonRelativeDateAnchor;
};

export type JsonDateFilter = {
    preset?: DateFilter['preset'];
    rule?: JsonRelativeDateRule;
    start: string | null;
    end: string | null;
};

type JsonFilterValueByType<TType extends FilterType> = TType extends 'string'
    ? string | null
    : TType extends 'string[]'
        ? string[] | null
        : TType extends 'number'
            ? NumberFilter | null
            : TType extends 'boolean'
                ? boolean | null
        : TType extends 'DateFilter'
            ? JsonDateFilter | null
            : never;

export type FilterValuesByDefinition<
    TDefinition extends Record<string, FilterType>,
> = {
    [K in keyof TDefinition]: FilterValueByType<TDefinition[K]>;
};

export type JsonFilterValuesByDefinition<
    TDefinition extends Record<string, FilterType>,
> = {
    [K in keyof TDefinition]: JsonFilterValueByType<TDefinition[K]>;
};

function toIso(value: DateTime | null): string | null {
    return value ? value.toISO() : null;
}

function fromIso(value: unknown): DateTime | null {
    if (value === null) {
        return null;
    }
    if (typeof value !== 'string') {
        return null;
    }
    const parsed = DateTime.fromISO(value);
    return parsed.isValid ? parsed : null;
}

function toJsonRule(rule: RelativeDateRule): JsonRelativeDateRule {
    return {
        ...rule,
        anchor:
            rule.anchor?.type === 'date'
                ? {type: 'date', value: rule.anchor.value.toISO() ?? ''}
                : rule.anchor,
    };
}

function toDateFilterJson(value: DateFilter): JsonDateFilter {
    return {
        preset: value.preset,
        rule: value.rule ? toJsonRule(value.rule) : undefined,
        start: toIso(value.start),
        end: toIso(value.end),
    };
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function parseStringArray(value: unknown): NonEmptyArray<string> | null {
    if (!Array.isArray(value)) {
        return null;
    }

    const parsed = value.filter(
        (item): item is string => typeof item === 'string',
    );
    if (parsed.length === 0) {
        return null;
    }

    return parsed as NonEmptyArray<string>;
}

function parseDateRule(value: unknown): RelativeDateRule | undefined {
    if (!isRecord(value)) {
        return undefined;
    }

    const anchorValue = value.anchor;
    const anchor =
        isRecord(anchorValue) && anchorValue.type === 'date'
            ? (() => {
                const parsed = fromIso(anchorValue.value);
                return parsed ? {type: 'date' as const, value: parsed} : undefined;
            })()
            : isRecord(anchorValue) && anchorValue.type === 'now'
                ? {type: 'now' as const}
                : undefined;

    const unit = value.unit;
    if (unit !== 'day' && unit !== 'week' && unit !== 'month') {
        return undefined;
    }

    if (value.kind === 'bounded') {
        const direction = value.direction;
        if (direction !== 'last' && direction !== 'next' && direction !== 'current') {
            return undefined;
        }

        const amount =
            typeof value.amount === 'number' ? value.amount : undefined;

        return {
            kind: 'bounded',
            direction,
            amount,
            unit,
            anchor,
        };
    }

    if (value.kind === 'open') {
        const direction = value.direction;
        if (direction !== 'before' && direction !== 'after') {
            return undefined;
        }
        if (typeof value.amount !== 'number') {
            return undefined;
        }

        return {
            kind: 'open',
            direction,
            amount: value.amount,
            unit,
            anchor,
        };
    }

    return undefined;
}

function parseDateFilter(value: unknown): DateFilter | null {
    if (!isRecord(value)) {
        return null;
    }

    return {
        preset:
            typeof value.preset === 'string'
                ? (value.preset as DateFilter['preset'])
                : undefined,
        rule: parseDateRule(value.rule),
        start: fromIso(value.start),
        end: fromIso(value.end),
    };
}

function parseNumberOperator(value: unknown): NumberOperator | undefined {
    return value === 'eq' ||
    value === 'neq' ||
    value === 'gt' ||
    value === 'gte' ||
    value === 'lt' ||
    value === 'lte' ||
    value === 'range'
        ? value
        : undefined;
}

function toFiniteNumber(value: unknown): number | undefined {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function parseNumberFilter(value: unknown): NumberFilter | null {
    if (!isRecord(value)) {
        return null;
    }

    const op = parseNumberOperator(value.op);
    if (!op) {
        return null;
    }

    return {
        op,
        value: toFiniteNumber(value.value),
        min: toFiniteNumber(value.min),
        max: toFiniteNumber(value.max),
    };
}

export function filterValueToJson<TType extends FilterType>(
    type: TType,
    value: FilterValueByType<TType>,
): JsonFilterValueByType<TType> {
    switch (type) {
        case 'string':
            return value as JsonFilterValueByType<TType>;
        case 'string[]':
            return value as JsonFilterValueByType<TType>;
        case 'number':
            return value as JsonFilterValueByType<TType>;
        case 'boolean':
            return value as JsonFilterValueByType<TType>;
        case 'DateFilter':
            return (
                value ? toDateFilterJson(value as DateFilter) : null
            ) as JsonFilterValueByType<TType>;
    }
}

export function filterValueFromJson<TType extends FilterType>(
    type: TType,
    value: unknown,
): FilterValueByType<TType> {
    switch (type) {
        case 'string':
            return (
                typeof value === 'string' ? value : null
            ) as FilterValueByType<TType>;
        case 'string[]':
            return parseStringArray(value) as FilterValueByType<TType>;
        case 'number':
            return parseNumberFilter(value) as FilterValueByType<TType>;
        case 'boolean':
            return (typeof value === 'boolean' ? value : null) as FilterValueByType<TType>;
        case 'DateFilter':
            return parseDateFilter(value) as FilterValueByType<TType>;
    }
}

export function filterValuesToJson<
    TFilters extends Record<string, unknown>,
    TDefinition extends FilterDefinition<TFilters>,
>(
    definition: TDefinition,
    values: FilterValuesByDefinition<TDefinition>,
): JsonFilterValuesByDefinition<TDefinition> {
    const json: Record<string, unknown> = {};

    function assignValue<K extends keyof TDefinition>(key: K) {
        const type = definition[key];
        switch (type) {
            case 'string':
                json[key as string] = filterValueToJson(
                    'string',
                    values[key] as FilterValueByType<'string'>,
                );
                return;
            case 'string[]':
                json[key as string] = filterValueToJson(
                    'string[]',
                    values[key] as FilterValueByType<'string[]'>,
                );
                return;
            case 'number':
                json[key as string] = filterValueToJson(
                    'number',
                    values[key] as FilterValueByType<'number'>,
                );
                return;
            case 'boolean':
                json[key as string] = filterValueToJson(
                    'boolean',
                    values[key] as FilterValueByType<'boolean'>,
                );
                return;
            case 'DateFilter':
                json[key as string] = filterValueToJson(
                    'DateFilter',
                    values[key] as FilterValueByType<'DateFilter'>,
                );
                return;
        }
    }

    for (const key of Object.keys(definition) as (keyof TDefinition)[]) {
        assignValue(key);
    }

    return json as JsonFilterValuesByDefinition<TDefinition>;
}

export function filterValuesFromJson<
    TFilters extends Record<string, unknown>,
    TDefinition extends FilterDefinition<TFilters>,
>(
    definition: TDefinition,
    json: Partial<Record<keyof TDefinition, unknown>>,
): FilterValuesByDefinition<TDefinition> {
    const values: Record<string, unknown> = {};

    function assignValue<K extends keyof TDefinition>(key: K) {
        const type = definition[key];
        switch (type) {
            case 'string':
                values[key as string] = filterValueFromJson(
                    'string',
                    json[key],
                );
                return;
            case 'string[]':
                values[key as string] = filterValueFromJson(
                    'string[]',
                    json[key],
                );
                return;
            case 'number':
                values[key as string] = filterValueFromJson(
                    'number',
                    json[key],
                );
                return;
            case 'boolean':
                values[key as string] = filterValueFromJson(
                    'boolean',
                    json[key],
                );
                return;
            case 'DateFilter':
                values[key as string] = filterValueFromJson(
                    'DateFilter',
                    json[key],
                );
                return;
        }
    }

    for (const key of Object.keys(definition) as (keyof TDefinition)[]) {
        assignValue(key);
    }

    return values as FilterValuesByDefinition<TDefinition>;
}
